import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import { get, getIn } from 'immutable';
import { generateForm } from '../../form/Form';
import { createTenant, fetchSystemDefaultTaskDbAdapter, fetchTenant, updateTenant } from '../../../apis/system';
import { slugify } from '../../../helpers';
import { VALIDATE_DB_ADAPTERS, ORACLE_FIELDS, MSSQL_FIELDS, POSTGRES_FIELDS, adapterProperties } from '../helpers';
import { handleFormErrors } from '../../form/Form.helpers';
var TENANT_INCLUDES = 'details';

var dataSources = function dataSources(_ref) {
  var slug = _ref.slug;
  return {
    tenant: {
      fn: fetchTenant,
      params: slug && [{
        slug: slug,
        include: TENANT_INCLUDES
      }],
      transform: function transform(result) {
        return result.tenant;
      }
    },
    defaultTaskDbAdapter: {
      fn: fetchSystemDefaultTaskDbAdapter,
      params: [],
      transform: function transform(result) {
        return result.adapter;
      }
    }
  };
};

var handleSubmit = function handleSubmit(_ref2) {
  var slug = _ref2.slug;
  return function (values) {
    var tenant = {
      space: {
        slug: values.get('slug'),
        name: values.get('name')
      },
      task: _objectSpread(_objectSpread({
        autoCreateDatabase: values.get('task_autoCreateDatabase') ? 'true' : 'false'
      }, slug ? {
        deployment: {
          image: values.get('image'),
          replicas: parseInt(values.get('replicas'))
        }
      } : {}), {}, {
        databaseAdapter: {
          type: values.get('task_databaseAdapter_type'),
          properties: adapterProperties(values, values.get('task_databaseAdapter_type'))
        }
      }),
      users: values.get('users')
    };
    return slug ? updateTenant({
      slug: slug,
      tenant: tenant
    }).then(handleFormErrors('space', 'There was an error saving the Space.')) : createTenant({
      tenant: tenant
    }).then(handleFormErrors(null, 'There was an error saving the Space.'));
  };
};

var getSpaceValue = function getSpaceValue(tenant, key) {
  return tenant ? getIn(tenant, ['space', key], '') : '';
};

var fields = function fields(_ref3) {
  var slug = _ref3.slug;
  return function (_ref4) {
    var tenant = _ref4.tenant,
        defaultTaskDbAdapter = _ref4.defaultTaskDbAdapter;
    return (tenant || !slug) && (defaultTaskDbAdapter || defaultTaskDbAdapter === null) && [// Top-level fields
    {
      name: 'authenticationSecret',
      label: 'Authentication Secret',
      type: 'password',
      visible: function visible(_ref5) {
        var values = _ref5.values;
        return values.get('changeAuthenticationSecret');
      },
      "transient": function transient(_ref6) {
        var values = _ref6.values;
        return !values.get('changeAuthenticationSecret');
      },
      required: false
    }, {
      name: 'changeAuthenticationSecret',
      label: 'Change Authentication Secret',
      type: 'checkbox',
      "transient": true,
      initialValue: false,
      onChange: function onChange(_ref7, _ref8) {
        var values = _ref7.values;
        var setValue = _ref8.setValue;

        if (values.get('authenticationSecret') !== '') {
          setValue('authenticationSecret', '');
        }
      }
    }, // Start - Space Fields
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      onChange: function onChange(_ref9, _ref10) {
        var values = _ref9.values;
        var setValue = _ref10.setValue;

        if (values.get('linked')) {
          setValue('slug', slugify(values.get('name')), false);
        }
      },
      initialValue: getSpaceValue(tenant, 'name')
    }, {
      name: 'slug',
      label: 'Slug',
      type: 'text',
      required: true,
      enabled: !slug,
      onChange: function onChange(_bindings, _ref11) {
        var setValue = _ref11.setValue;
        setValue('linked', false);
      },
      initialValue: getSpaceValue(tenant, 'slug')
    }, {
      name: 'linked',
      label: 'Linked',
      type: 'checkbox',
      "transient": true,
      initialValue: !tenant,
      visible: false
    }, // End - Space Fields
    // Start - Task fields
    {
      name: 'replicas',
      label: 'Task Replica Count',
      type: 'text',
      required: !!slug,
      initialValue: getIn(tenant, ['task', 'deployment', 'replicas'], 1) // pattern: /^\d+$/,
      // patternMessage:
      //   'You must provide a number of replicas desired.',

    }, {
      name: 'image',
      label: 'Task Image',
      type: 'text',
      required: !!slug,
      initialValue: getIn(tenant, ['task', 'deployment', 'image'])
    }, {
      name: 'task_databaseAdapter_type',
      label: 'Task Adapter',
      required: true,
      type: 'select',
      options: VALIDATE_DB_ADAPTERS,
      initialValue: getIn(tenant, ['task', 'databaseAdapter', 'type'], get(defaultTaskDbAdapter, 'type', ''))
    }, {
      name: 'task_autoCreateDatabase',
      label: 'Auto-Create Database',
      type: 'checkbox',
      visible: function visible(_ref12) {
        var values = _ref12.values;
        return values.get('task_databaseAdapter_type') === 'postgres';
      },
      "transient": function transient(_ref13) {
        var values = _ref13.values;
        return values.get('task_databaseAdapter_type') !== 'postgres';
      },
      initialValue: slug ? get(tenant, ['task', 'autoCreateDatabase'], true) : true
    }].concat(_toConsumableArray(MSSQL_FIELDS('task_databaseAdapter_type', tenant, ['task', 'databaseAdapter'], defaultTaskDbAdapter)), _toConsumableArray(ORACLE_FIELDS('task_databaseAdapter_type', tenant, ['task', 'databaseAdapter'], defaultTaskDbAdapter)), _toConsumableArray(POSTGRES_FIELDS('task_databaseAdapter_type', tenant, ['task', 'databaseAdapter'], defaultTaskDbAdapter)), _toConsumableArray(slug ? [] : [{
      name: 'username',
      label: 'Username',
      type: 'text',
      "transient": true,
      required: true,
      enabled: true,
      visible: true,
      initialValue: ''
    }, {
      name: 'email',
      label: 'Email',
      type: 'text',
      "transient": true,
      required: true,
      enabled: true,
      visible: true,
      initialValue: ''
    }, {
      name: 'displayName',
      label: 'Display Name',
      type: 'text',
      "transient": true,
      required: true,
      enabled: true,
      visible: true,
      initialValue: ''
    }, {
      name: 'password',
      label: 'Password',
      type: 'password',
      "transient": true,
      required: true,
      enabled: true,
      visible: true
    }, {
      name: 'passwordConfirmation',
      label: 'Password Confirmation',
      type: 'password',
      "transient": true,
      required: true,
      enabled: true,
      visible: true,
      constraint: function constraint(_ref14) {
        var values = _ref14.values;
        return values.get('passwordConfirmation') === values.get('password');
      },
      constraintMessage: 'Password Confirmation does not match'
    }, {
      name: 'users',
      label: 'Admin User',
      type: null,
      visible: false,
      required: false,
      serialize: function serialize(_ref15) {
        var values = _ref15.values;
        return [{
          username: values.get('username'),
          password: values.get('password'),
          email: values.get('email'),
          displayName: values.get('displayName')
        }];
      }
    }]));
  };
};

export var SystemTenantForm = generateForm({
  formOptions: ['slug'],
  dataSources: dataSources,
  fields: fields,
  handleSubmit: handleSubmit
});